import { createTheme } from '@mui/material/styles';

// ---------------------------------------------------------------------------
// Pigeon Pedigree Manager — design system
//
// Goals: a calm, professional "high-grade software" feel. The levers that do
// the heavy lifting here are (1) a cool-neutral palette with a proper brand
// ramp, (2) soft, layered shadows instead of MUI's harsh grey ones, (3) a
// refined type scale with tighter headings and no shouty ALL-CAPS buttons, and
// (4) slightly larger, consistent radii. Everything else inherits from these.
// ---------------------------------------------------------------------------

const brand = {
  lighter: '#eaf1fb',
  light: '#6d97cf',
  main: '#2c5aa0',
  dark: '#1d3f74',
  darker: '#14294c'
};

// Cool-tinted neutral ramp — greys with a faint blue bias read as "designed"
// rather than the default flat mid-grey.
const grey = {
  50: '#f6f8fb',
  100: '#eef1f6',
  200: '#e3e8ef',
  300: '#cdd5e0',
  400: '#9aa6b7',
  500: '#6b7688',
  600: '#4d5766',
  700: '#39414e',
  800: '#232a34',
  900: '#141922'
};

// Soft, layered shadows (cool shadow colour). MUI requires exactly 25 entries.
const s = (a, b) => `${a}, ${b}`;
const softShadow = '0 6px 16px -6px rgba(20,30,55,0.16), 0 2px 6px -2px rgba(20,30,55,0.10)';
const shadows = [
  'none',
  s('0 1px 2px rgba(20,30,55,0.04)', '0 1px 2px rgba(20,30,55,0.06)'),
  s('0 1px 3px rgba(20,30,55,0.05)', '0 2px 6px -1px rgba(20,30,55,0.08)'),
  s('0 2px 5px -1px rgba(20,30,55,0.06)', '0 6px 12px -3px rgba(20,30,55,0.09)'),
  s('0 3px 6px -2px rgba(20,30,55,0.06)', '0 10px 20px -6px rgba(20,30,55,0.12)'),
  s('0 4px 8px -3px rgba(20,30,55,0.07)', '0 14px 26px -8px rgba(20,30,55,0.13)'),
  s('0 5px 10px -3px rgba(20,30,55,0.08)', '0 18px 32px -10px rgba(20,30,55,0.14)'),
  softShadow,
  s('0 8px 18px -6px rgba(20,30,55,0.10)', '0 24px 44px -12px rgba(20,30,55,0.18)')
];
while (shadows.length < 25) {
  shadows.push('0 12px 32px -10px rgba(20,30,55,0.22)');
}

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { ...brand, contrastText: '#ffffff' },
    secondary: { light: '#e56aa3', main: '#cf3d81', dark: '#a12a64', contrastText: '#ffffff' },
    info: { light: '#5aa2e6', main: '#2f7fd1', dark: '#1f5c9e', contrastText: '#ffffff' },
    success: { light: '#5cc08a', main: '#2e9e6b', dark: '#1f7a50', contrastText: '#ffffff' },
    warning: { light: '#f0c14b', main: '#d99a1c', dark: '#a9760f', contrastText: '#ffffff' },
    error: { light: '#e57373', main: '#d64545', dark: '#a72f2f', contrastText: '#ffffff' },
    grey,
    divider: '#e6eaf1',
    background: { default: '#f4f6fa', paper: '#ffffff' },
    text: {
      primary: '#1a2230',
      secondary: '#5b6675',
      disabled: '#9aa6b7'
    },
    action: {
      hover: 'rgba(44,90,160,0.06)',
      selected: 'rgba(44,90,160,0.10)',
      active: '#5b6675'
    }
  },

  shape: { borderRadius: 10 },
  shadows,

  typography: {
    fontFamily: [
      '"Segoe UI Variable Text"', '"Segoe UI"', '-apple-system', 'BlinkMacSystemFont',
      '"Inter"', 'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif'
    ].join(','),
    h1: { fontWeight: 700, letterSpacing: '-0.022em' },
    h2: { fontWeight: 700, letterSpacing: '-0.021em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700, letterSpacing: '-0.019em', fontSize: '1.7rem' },
    h5: { fontWeight: 700, letterSpacing: '-0.015em', fontSize: '1.3rem' },
    h6: { fontWeight: 650, letterSpacing: '-0.01em', fontSize: '1.075rem' },
    subtitle1: { fontWeight: 600, letterSpacing: '-0.006em' },
    subtitle2: { fontWeight: 600, letterSpacing: '-0.004em' },
    body1: { lineHeight: 1.6 },
    body2: { lineHeight: 1.6 },
    overline: {
      fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.68rem',
      textTransform: 'uppercase', lineHeight: 1.6
    },
    button: { fontWeight: 600, letterSpacing: 0, textTransform: 'none' },
    caption: { letterSpacing: 0 }
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f4f6fa',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale'
        },
        '::selection': { backgroundColor: 'rgba(44,90,160,0.18)' },
        // Slim, unobtrusive scrollbars that match the palette.
        '*::-webkit-scrollbar': { width: 11, height: 11 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: '#cdd5e0',
          borderRadius: 8,
          border: '3px solid transparent',
          backgroundClip: 'content-box'
        },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9aa6b7' },
        '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' }
      }
    },

    MuiPaper: {
      styleOverrides: {
        // MUI paints a translucent overlay on elevated Paper; kill it so our
        // shadows/borders define depth cleanly.
        root: { backgroundImage: 'none' }
      }
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: '1px solid #e6eaf1',
          borderRadius: 14,
          boxShadow: '0 1px 2px rgba(20,30,55,0.04), 0 2px 8px -2px rgba(20,30,55,0.06)'
        }
      }
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 9, paddingLeft: 16, paddingRight: 16, paddingTop: 7, paddingBottom: 7 },
        sizeLarge: { paddingTop: 10, paddingBottom: 10, paddingLeft: 22, paddingRight: 22, fontSize: '0.95rem' },
        sizeSmall: { paddingTop: 4, paddingBottom: 4 },
        contained: {
          boxShadow: '0 1px 2px rgba(20,30,55,0.10), 0 2px 6px -2px rgba(44,90,160,0.28)',
          '&:hover': { boxShadow: '0 2px 4px rgba(20,30,55,0.12), 0 6px 14px -4px rgba(44,90,160,0.36)' }
        },
        outlined: { borderColor: '#d3dbe6' }
      }
    },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 8 },
        sizeSmall: { borderRadius: 7, height: 22, fontSize: '0.72rem' },
        outlined: { borderColor: '#d3dbe6' }
      }
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 9,
          backgroundColor: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d9dfe9' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#b7c1d1' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 }
        }
      }
    },

    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 9 }
      }
    },

    MuiTableHead: {
      styleOverrides: {
        root: { backgroundColor: '#f6f8fb' }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: '#eceff4' },
        head: {
          fontWeight: 700,
          fontSize: '0.7rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: '#5b6675'
        }
      }
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 18,
          boxShadow: '0 24px 60px -12px rgba(20,30,55,0.28)'
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: '1px solid #e6eaf1',
          boxShadow: '0 10px 32px -8px rgba(20,30,55,0.22)'
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#232a34',
          borderRadius: 8,
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: '6px 10px'
        }
      }
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 11, fontSize: '0.875rem' },
        standardInfo: { backgroundColor: '#eef4fc', color: '#1d3f74' },
        standardSuccess: { backgroundColor: '#e9f6ef' },
        standardWarning: { backgroundColor: '#fbf3e2' },
        standardError: { backgroundColor: '#fbecec' }
      }
    },

    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 600 }
      }
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: '#e6eaf1' } }
    },

    MuiToggleButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderColor: '#d9dfe9' }
      }
    }
  }
});

export default theme;
